/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-empty-function */

import {
  useState,
  createContext,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { useParams } from "react-router-dom";
import genericStyles from "../../Styles/genericStyles.module.css";
import MUIDataTableCustomStyle from "../../Components/MUIDataTableCustomStyle";
import { Vocabulary } from "../../Utils/Vocabulary";
import { endpoints, localUrlEnum } from "../../Utils/UrlEnum";
import { ClientModel } from "../../Utils/Models";
import {
  Chip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
  Typography,
  TextField,
} from "@mui/material";
import { formatDate, OrderStatusType, setFontColorBasedOnBackground } from "../../Utils/Utils";
import { ETableEditAction } from "../../Utils/Enums";
import { TablesIds } from "../../Utils/Constants";
import { getData } from "../../Services/getData";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import { updateData } from "../../Services/updateData";
import { postData } from "../../Services/postData";
import useCustomMemo from "../../Hooks/useCustomMemo";
import GenericModal from "../GenericModal";

export const CRUDContext = createContext(new ClientModel());

export default function ClassClients() {
  const { classId } = useParams();
  const [contextValue, setContextValue] = useState(new ClientModel());
  const [className, setClassName] = useState<string>("");
  const endPointUrl = `${endpoints.classesEndPoint}/${classId}/clients`;
  const [selectedExamsData, setSelectedExamsData] = useState<any[]>([]);
  const [bulkStatusDialogOpen, setBulkStatusDialogOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [smsModalOpen, setSmsModalOpen] = useState(false);
  const [smsMessage, setSmsMessage] = useState("");
  const memo = useCustomMemo();

  useEffect(() => {
    if (classId) {
      // Fetch class details to get the class name
      getData(`${endpoints.classesEndPoint}/${classId}`)
        .then((response: any) => {
          if (response?.data) {
            setClassName(response.data.name || "");
          }
        })
        .catch(() => {
          // Handle error silently or with appropriate error handling
        });
    }
  }, [classId]);

  /**
   * Handles bulk status update for selected exams and their related orders
   */
  const handleBulkStatusUpdate = useCallback(async () => {
    try {
      const updatePromises = selectedExamsData.map(async (examData: any) => {
        const orderId = examData[examData.length - 3]; // Third to last column is _id (index 9)

        if (orderId) {
          return updateData(`${endpoints.ordersEndpoint}/${orderId}`, {
            status: selectedStatus,
          });
        }
      });

      await Promise.all(updatePromises);
      setBulkStatusDialogOpen(false);
      setSelectedStatus("");
      setSelectedExamsData([]);
    } catch (error) {
      console.error("Error updating exam statuses and related orders:", error);
    }
  }, [selectedExamsData, selectedStatus]);

  /**
   * Opens the bulk status update dialog and sets the selected exams data
   */
  const handleOpenBulkDialog = useCallback(
    (selectedRowsData: any, displayData: any) => {
      const selectedExams = selectedRowsData.data.map(
        (row: any) => displayData[row.dataIndex].data,
      );
      setSelectedExamsData(selectedExams);
      setBulkStatusDialogOpen(true);
    },
    [],
  );

  /**
   * Handles sending SMS to selected clients
   */
  const handleSendSms = useCallback(async () => {
    try {
      const clientIds = selectedExamsData
        .map(
          (examData: any) => examData[examData.length - 2], // clientId is at second-to-last position (index 10)
        )
        .filter(Boolean);

      await postData(endpoints.notifications.sendSMSMessages, {
        clientIds,
        message: smsMessage,
      });

      setSmsModalOpen(false);
      setSmsMessage("");
      setSelectedExamsData([]);
    } catch (error) {
      console.error("Error sending SMS:", error);
    }
  }, [selectedExamsData, smsMessage]);

  /**
   * Opens the SMS modal and sets the selected exams data
   */
  const handleOpenSmsModal = useCallback(
    (selectedRowsData: any, displayData: any) => {
      const selectedExams = selectedRowsData.data.map(
        (row: any) => displayData[row.dataIndex].data,
      );
      setSelectedExamsData(selectedExams);
      setSmsModalOpen(true);
    },
    [],
  );

  /**
   * Custom toolbar for bulk actions on selected rows
   */
  const customToolbarSelect = useCallback(
    (selectedRowsData: any, displayData: any) => {
      return (
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#1976D2",
              color: "white",
              fontWeight: 600,
              fontSize: "0.95rem",
              padding: "10px 24px",
              boxShadow: "0 2px 6px rgba(25, 118, 210, 0.3)",
              "&:hover": {
                backgroundColor: "#1565C0",
                boxShadow: "0 4px 12px rgba(25, 118, 210, 0.4)",
              },
            }}
            onClick={() => handleOpenBulkDialog(selectedRowsData, displayData)}
          >
            {`${Vocabulary.edit} ${Vocabulary.status} (${selectedRowsData.data.length})`}
          </Button>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#4CAF50",
              color: "white",
              fontWeight: 600,
              fontSize: "0.95rem",
              padding: "10px 24px",
              boxShadow: "0 2px 6px rgba(76, 175, 80, 0.3)",
              "&:hover": {
                backgroundColor: "#45a049",
                boxShadow: "0 4px 12px rgba(76, 175, 80, 0.4)",
              },
            }}
            onClick={() => handleOpenSmsModal(selectedRowsData, displayData)}
          >
            {Vocabulary.sendSMS}
          </Button>
        </Box>
      );
    },
    [handleOpenBulkDialog, handleOpenSmsModal],
  );

  /**
   * Handles row click to navigate to order details in a new tab
   */
  const handleRowClick = useCallback((rowData: any) => {
    const orderId = rowData?._id || rowData?.orderId;
    if (orderId) {
      const orderUrl = `${localUrlEnum.order}/${orderId}`;
      window.open(orderUrl, "_blank");
    }
  }, []);

  // Filter order statuses for certificate types - memoized to prevent recalculation
  const schoolTypeStatuses = useMemo(() => {
    return (
      memo?.storage?.orderStatuses?.filter(
        (status: any) =>
          status.typeId?.name == OrderStatusType.License,
      ) || []
    );
  }, [memo?.storage?.orderStatuses]);


  const clientsColumns = [
    {
      label: `${Vocabulary.firstName}`,
      name: "orderId",
      options: {
        sort: false,
        display: false,
        customBodyRender: (orderId: any) => {
          return orderId || "";
        },
      },
    },
    {
      label: `${Vocabulary.status}`,
      name: "orderStatus",
      options: {
        setCellHeaderProps: () => ({
          align: "center",
        }),
        setCellProps: () => ({
          align: "center",
        }),
        filter: false,
        sort: false,
        empty: true,
        customBodyRender: (rowData: any) => {
          return rowData?._id ? (
            <>
              <Chip
                color="primary"
                key={rowData?._id}
                label={rowData?.name}
                style={{
                  margin: 5,
                  backgroundColor: rowData?.color,
                  color: setFontColorBasedOnBackground(rowData?.color),
                }}
              />
            </>
          ) : (
            ""
          );
        },
      },
    },
    {
      label: `${Vocabulary.lastName}`,
      name: "clientName",
      options: {
        sort: true,
      },
    },
    {
      label: `${Vocabulary.phone}`,
      name: "phone",
      options: {
        setCellHeaderProps: () => ({
          align: "center",
        }),
        setCellProps: () => ({
          align: "center",
          minHeight: 100,
        }),
        filter: false,
        sort: false,
        empty: true,
        customBodyRender: (phone: string) => {
          return (
            <>
              {phone ? (
                <a
                  key={phone}
                  href={`tel:${phone}`}
                  style={{ textDecoration: "none" }}
                >
                  <Chip
                    icon={<LocalPhoneIcon fontSize="small" />}
                    color="primary"
                    label={phone}
                    style={{ padding: 5 }}
                  />
                </a>
              ) : null}
            </>
          );
        },
      },
    },
    {
      label: `${Vocabulary.remainingAmount}`,
      name: "remainingAmount",
      options: {
        sort: true,
        customBodyRender: (value: number) => {
          return (
            <span style={{ color: "#ff0000", fontWeight: 600 }}>{value}</span>
          );
        },
      },
    },
    {
      label: `${Vocabulary.instructor}`,
      name: "instructorName",
      options: {
        sort: true,
      },
    },
    {
      label: `${Vocabulary.course}`,
      name: "product",
      options: {
        sort: true,
      },
    },
    {
      label: `${Vocabulary.drivingHours}`,
      name: "drivingHours",
      options: {
        sort: true,
      },
    },
    {
      label: Vocabulary.createdAt,
      name: "dateAdded",
      options: {
        sort: true,
        customBodyRender: (value: string) => {
          return formatDate(value);
        },
      },
    },
    /** Hidden (used for bulk operations) DON'T DELETE */
    {
      label: "Order ID",
      name: "_id",
      options: {
        display: false,
      },
    },
    /** Hidden (used for bulk SMS) DON'T DELETE */
    {
      label: Vocabulary.clientId,
      name: "clientId",
      options: {
        display: false,
      },
    },
  ];

  return (
    <div className={genericStyles.mainContainer}>
      <CRUDContext.Provider value={contextValue}>
        <MUIDataTableCustomStyle
          onEditAction={ETableEditAction.REDIRECT}
          title={`${Vocabulary.clients} - ${className}`}
          columns={clientsColumns}
          CRUDEndPoint={endPointUrl}
          id={TablesIds.clients}
          setContextValue={setContextValue}
          rowClickURL={localUrlEnum.client}
          hideAddButton={true}
          hideEditButton={true}
          hideDeleteButton={true}
          onRowClick={handleRowClick}
          selectableRows={"multiple"}
          customToolbarSelect={customToolbarSelect}
          showPrintButton
          showDownloadButton
        />
      </CRUDContext.Provider>
      <GenericModal
        open={bulkStatusDialogOpen}
        onClose={() => setBulkStatusDialogOpen(false)}
        title={`${Vocabulary.edit} ${Vocabulary.status}`}
        width="600px"
        actions={
          <div className={genericStyles.modalActions}>
            <Button onClick={() => setBulkStatusDialogOpen(false)}>
              {Vocabulary.cancel}
            </Button>
            <Button
              onClick={handleBulkStatusUpdate}
              variant="contained"
              disabled={!selectedStatus}
            >
              {Vocabulary.save}
            </Button>
          </div>
        }
      >
        <Box sx={{ minWidth: 200, mt: 2 }}>
          <Alert
            severity="warning"
            sx={{
              mb: 3,
              backgroundColor: "#fff3cd",
              border: "1px solid #ffc107",
              borderRadius: "8px",
              "& .MuiAlert-icon": {
                color: "#856404",
              },
              "& .MuiAlert-message": {
                color: "#856404",
                fontWeight: 500,
              },
            }}
          >
            <Typography variant="body2">{Vocabulary.bulkWarning}</Typography>
          </Alert>
          <FormControl fullWidth>
            <InputLabel>{Vocabulary.status}</InputLabel>
            <Select
              value={selectedStatus}
              label={Vocabulary.status}
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              {schoolTypeStatuses.map((status: any) => (
                <MenuItem key={status._id} value={status._id}>
                  {status.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </GenericModal>

      <GenericModal
        open={smsModalOpen}
        onClose={() => {
          setSmsModalOpen(false);
          setSmsMessage("");
        }}
        title={Vocabulary.sendSMS}
        width="600px"
        actions={
          <div className={genericStyles.modalActions}>
            <Button
              onClick={() => {
                setSmsModalOpen(false);
                setSmsMessage("");
              }}
            >
              {Vocabulary.cancel}
            </Button>
            <Button
              onClick={handleSendSms}
              variant="contained"
              disabled={!smsMessage.trim()}
            >
              {Vocabulary.send}
            </Button>
          </div>
        }
      >
        <Box sx={{ minWidth: 200, mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            {`${Vocabulary.cursant}i selectati (${selectedExamsData.length}):`}
          </Typography>
          <Box
            sx={{
              maxHeight: 150,
              overflowY: "auto",
              mb: 3,
              p: 2,
              backgroundColor: "#f5f5f5",
              borderRadius: 1,
              border: "1px solid #e0e0e0",
            }}
          >
            {selectedExamsData.map((examData: any, index: number) => {
              const clientName = examData[2]; // clientName is at index 2
              return (
                <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                  {clientName}
                </Typography>
              );
            })}
          </Box>
          <TextField
            fullWidth
            multiline
            rows={4}
            label={Vocabulary.message}
            value={smsMessage}
            onChange={(e) => setSmsMessage(e.target.value)}
            placeholder="Introduceți mesajul SMS..."
          />
        </Box>
      </GenericModal>
    </div>
  );
}
